import {
  AuditOutlined,
  DownloadOutlined,
  FileTextOutlined,
  PrinterOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, DatePicker, Empty, Row, Col, Space, Statistic, Table, Tabs, Tag, Typography, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";

const { RangePicker } = DatePicker;
const money = (value: unknown) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const AccountingReportsPanel = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf("month"), dayjs()]);
  const [coa, setCoa] = useState<any[]>([]);
  const [trial, setTrial] = useState<any>();
  const [profitLoss, setProfitLoss] = useState<any>();
  const [balanceSheet, setBalanceSheet] = useState<any>();
  const [dayBook, setDayBook] = useState<any>();
  const [gst, setGst] = useState<any>();
  const [loading, setLoading] = useState(false);

  const dates = () => [range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")] as const;

  const loadReports = async () => {
    setLoading(true);
    try {
      const [from, to] = dates();
      const [coaData, tb, pl, bs, db, gstData] = await Promise.all([
        transportApi.getChartOfAccounts(),
        transportApi.getTrialBalance(to),
        transportApi.getProfitLoss(from, to),
        transportApi.getBalanceSheet(to),
        transportApi.getDayBook(from, to),
        transportApi.getGstReport(from, to),
      ]);
      setCoa(coaData ?? []);
      setTrial(tb);
      setProfitLoss(pl);
      setBalanceSheet(bs);
      setDayBook(db);
      setGst(gstData);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to load accounting reports"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadReports(); }, []);

  const bootstrap = async () => {
    setLoading(true);
    try {
      const result = await transportApi.bootstrapStandardCoa();
      message.success(result.message);
      await loadReports();
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to prepare the standard COA"));
      setLoading(false);
    }
  };

  const exportCoa = async () => {
    try {
      const blob = await transportApi.exportChartOfAccounts();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Chart-of-Accounts-${dayjs().format("YYYYMMDD")}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Unable to export the COA"));
    }
  };

  const reportToolbar = (
    <div className="accounting-report-toolbar">
      <Space wrap>
        <RangePicker value={range} onChange={(value) => value?.[0] && value[1] && setRange([value[0], value[1]])} />
        <Button type="primary" loading={loading} onClick={() => void loadReports()}>Refresh Reports</Button>
      </Space>
      <Space>
        <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
        <Button icon={<DownloadOutlined />} onClick={() => void exportCoa()}>Export COA</Button>
      </Space>
    </div>
  );

  const coaTab = (
    <>
      <Alert showIcon type="info" icon={<SafetyCertificateOutlined />} message="Indian statutory-ready Chart of Accounts"
        description="Standard groups include cash and bank, receivables, payables, GST input/output, TDS, capital, income, direct and indirect expenses." action={<Button onClick={() => void bootstrap()}>Prepare Standard COA</Button>} />
      <Table rowKey="id" size="small" loading={loading} dataSource={coa} pagination={{ pageSize: 25 }} scroll={{ x: 850 }}
        columns={[
          { title: "Ledger", dataIndex: "name", fixed: "left", width: 220 },
          { title: "Type", dataIndex: "accountType", width: 110 },
          { title: "Group", dataIndex: "accountGroup", width: 180 },
          { title: "Category", dataIndex: "category", width: 170 },
          { title: "Control", width: 100, render: (_, row) => row.isSystemLedger ? <Tag color="blue">System</Tag> : <Tag>Manual</Tag> },
          { title: "Balance", align: "right", width: 150, render: (_, row) => `${money(row.balance)} ${row.balanceSide}` },
        ]} />
    </>
  );

  const trialTab = (
    <>
      <Row gutter={12} className="accounting-report-summary">
        <Col xs={24} sm={8}><Statistic title="Total Debit" value={trial?.totalDebit ?? 0} precision={2} prefix="₹" /></Col>
        <Col xs={24} sm={8}><Statistic title="Total Credit" value={trial?.totalCredit ?? 0} precision={2} prefix="₹" /></Col>
        <Col xs={24} sm={8}><Statistic title="Status" value={trial?.isBalanced ? "Balanced" : "Difference"} valueStyle={{ color: trial?.isBalanced ? "var(--success)" : "var(--danger)" }} /></Col>
      </Row>
      <Table rowKey="ledgerId" size="small" loading={loading} dataSource={trial?.lines ?? []} pagination={false} scroll={{ x: 900 }}
        columns={[
          { title: "Ledger", dataIndex: "ledgerName", fixed: "left", width: 220 },
          { title: "Category", dataIndex: "accountCategory", width: 150 },
          { title: "Opening Dr", dataIndex: "openingDebit", align: "right", render: money },
          { title: "Opening Cr", dataIndex: "openingCredit", align: "right", render: money },
          { title: "Period Dr", dataIndex: "periodDebit", align: "right", render: money },
          { title: "Period Cr", dataIndex: "periodCredit", align: "right", render: money },
          { title: "Closing Dr", dataIndex: "closingDebit", align: "right", render: money },
          { title: "Closing Cr", dataIndex: "closingCredit", align: "right", render: money },
        ]} />
    </>
  );

  const statementTable = (groups: any[] = []) => (
    <Table rowKey={(row) => `${row.groupName}-${row.accountCategory}`} size="small" pagination={false} dataSource={groups}
      expandable={{ expandedRowRender: (row) => <Table rowKey="ledgerId" size="small" pagination={false} showHeader={false} dataSource={row.ledgers}
        columns={[{ dataIndex: "ledgerName" }, { dataIndex: "amount", align: "right", render: money }]} /> }}
      columns={[{ title: "Group", dataIndex: "groupName" }, { title: "Amount", dataIndex: "total", align: "right", render: money }]} />
  );

  const profitLossTab = (
    <Row gutter={16}>
      <Col xs={24} lg={12}><Card size="small" title={`Expenses · ₹${money(profitLoss?.totalExpenses)}`}>{statementTable(profitLoss?.expenseGroups)}</Card></Col>
      <Col xs={24} lg={12}><Card size="small" title={`Income · ₹${money(profitLoss?.totalIncome)}`}>{statementTable(profitLoss?.incomeGroups)}</Card></Col>
      <Col span={24}><Alert className="accounting-net-result" type={(profitLoss?.netProfit ?? 0) >= 0 ? "success" : "error"} showIcon message={`${(profitLoss?.netProfit ?? 0) >= 0 ? "Net Profit" : "Net Loss"}: ₹${money(Math.abs(profitLoss?.netProfit ?? 0))}`} /></Col>
    </Row>
  );

  const balanceSheetTab = (
    <>
      <Alert type={balanceSheet?.isBalanced ? "success" : "warning"} showIcon message={balanceSheet?.isBalanced ? "Balance Sheet is balanced" : `Difference: ₹${money(balanceSheet?.difference)}`} />
      <Row gutter={16}>
        <Col xs={24} lg={12}><Card size="small" title={`Liabilities + Capital · ₹${money(balanceSheet?.liabilities?.total)}`}>{statementTable(balanceSheet?.liabilities?.groups)}</Card></Col>
        <Col xs={24} lg={12}><Card size="small" title={`Assets · ₹${money(balanceSheet?.assets?.total)}`}>{statementTable(balanceSheet?.assets?.groups)}</Card></Col>
      </Row>
    </>
  );

  const dayBookTab = <Table rowKey="journalEntryId" size="small" loading={loading} dataSource={dayBook?.entries ?? []} pagination={{ pageSize: 25 }}
    columns={[{ title: "Date", dataIndex: "date", render: (v) => dayjs(v).format("DD/MM/YYYY") }, { title: "Voucher", dataIndex: "voucherType" }, { title: "Module", dataIndex: "sourceModule" }, { title: "Reference", dataIndex: "referenceNo" }, { title: "Amount", dataIndex: "entryTotal", align: "right", render: money }]} />;

  const gstTab = <><Row gutter={12} className="accounting-report-summary">
    <Col xs={12} md={6}><Statistic title="Taxable Value" value={gst?.totalTaxableValue ?? 0} precision={2} prefix="₹" /></Col>
    <Col xs={12} md={6}><Statistic title="CGST" value={gst?.totalCGST ?? 0} precision={2} prefix="₹" /></Col>
    <Col xs={12} md={6}><Statistic title="SGST" value={gst?.totalSGST ?? 0} precision={2} prefix="₹" /></Col>
    <Col xs={12} md={6}><Statistic title="IGST" value={gst?.totalIGST ?? 0} precision={2} prefix="₹" /></Col>
  </Row><Table rowKey="journalEntryId" size="small" loading={loading} dataSource={gst?.salesLines ?? []} locale={{ emptyText: <Empty description="No GST transactions in this period" /> }} scroll={{ x: 1000 }}
    columns={[{ title: "Date", dataIndex: "date", render: (v) => dayjs(v).format("DD/MM/YYYY") }, { title: "Invoice", dataIndex: "invoiceNo" }, { title: "Party", dataIndex: "partyName" }, { title: "GSTIN", dataIndex: "partyGSTIN" }, { title: "SAC", dataIndex: "sacCode" }, { title: "Taxable", dataIndex: "taxableAmount", align: "right", render: money }, { title: "CGST", dataIndex: "cgstAmount", align: "right", render: money }, { title: "SGST", dataIndex: "sgstAmount", align: "right", render: money }, { title: "IGST", dataIndex: "igstAmount", align: "right", render: money }]} /></>;

  return <section className="accounting-reports-surface">
    <div className="accounting-report-heading"><div><Typography.Title level={4}><AuditOutlined /> Financial Statements</Typography.Title><Typography.Text type="secondary">Auditable COA reports scoped to the selected organization and financial year.</Typography.Text></div><Tag icon={<FileTextOutlined />} color="blue">CA Review Pack</Tag></div>
    {reportToolbar}
    <Tabs type="card" className="accounting-report-tabs" items={[
      { key: "coa", label: "Chart of Accounts", children: coaTab },
      { key: "trial", label: "Trial Balance", children: trialTab },
      { key: "pl", label: "Profit & Loss", children: profitLossTab },
      { key: "bs", label: "Balance Sheet", children: balanceSheetTab },
      { key: "day", label: "Day Book", children: dayBookTab },
      { key: "gst", label: "GST", children: gstTab },
    ]} />
  </section>;
};
